/**
 * Deciding whether a search is worth writing down, and what to write.
 *
 * A schedule search runs on every keystroke, so most of what a teacher types is not a question
 * — it is the first two letters of one. Recording all of it would fill the table with `g`,
 * `gr`, `gra` and teach nobody anything.
 *
 * Worse, a search box is somewhere people paste. An address, a password meant for the field
 * above, a whole sentence copied out of an email. None of that is a question about the
 * programme and none of it should end up in a table WVCEA reads, so the rules below are a
 * gate rather than a tidy-up: anything that does not look like somebody looking for a session
 * is refused outright rather than trimmed until it fits.
 */

/** Below this it is somebody still typing. */
export const SHORTEST_TERM = 2;

/** Above this it is not a search, it is something that was pasted. */
export const LONGEST_TERM = 60;

/**
 * Anything with one of these in it is refused whole.
 *
 * An `@` is an address, a digit run that long is an identifier or a card, and a slash is a
 * link. None belongs in a list of what teachers wanted to learn about.
 */
const NOT_A_QUESTION = /[@<>{}\\|]|https?:|\d{5,}/iu;

/**
 * The term as it should be stored, or `null` when it should not be.
 *
 * Lower-cased and collapsed so that `Grammar`, `grammar ` and `grammar  click` do not become
 * three separate rows saying the same thing.
 */
export function normalizeSearchTerm(raw: string): string | null {
  if (typeof raw !== "string") return null;
  const collapsed = raw.replace(/\s+/gu, " ").trim().toLocaleLowerCase("en-US");
  if (collapsed.length < SHORTEST_TERM || collapsed.length > LONGEST_TERM) return null;
  if (NOT_A_QUESTION.test(collapsed)) return null;
  // At least one letter. A search for "..." or "2026" is not a subject anybody wanted.
  if (!/\p{L}/u.test(collapsed)) return null;
  return collapsed;
}

/**
 * Whether a longer term supersedes one already recorded from the same typing.
 *
 * The client only sends a term once the teacher has stopped typing, but she may stop twice on
 * the way to the same question — "gram" then "grammar". Treating the shorter as a question in
 * its own right would count one search as two and inflate whatever she was looking for.
 */
export function supersedesSearchTerm(previous: string | null, next: string): boolean {
  if (!previous) return false;
  return next.startsWith(previous) && next.length > previous.length;
}
